/**
 * ProfileTab — first tab of the consumer Profile page. Two-column layout:
 *
 *   Left  (1.5fr) — Identity card: avatar + name + email + verified chip +
 *                    3 read-only contact rows (Full name / Email / Phone) +
 *                    Edit / Change password actions.
 *   Right (1fr)   — Two stacked cards:
 *                    1. Account (2FA / sessions / member since + Sign out
 *                       everywhere danger button)
 *                    2. Quick actions (Download data / Connect Plaid /
 *                       Add co-borrower)
 *
 * Edit mode swaps the read-only rows for `MagicInput` form fields (the
 * existing pattern from the legacy ProfilePage) so we preserve the working
 * save / cancel flow against `useUpdateProfile`.
 *
 * Net-new actions (2FA enrollment, sessions panel, sign-out-everywhere,
 * download data, Plaid, add co-borrower) are wired to `// TODO(#…)` callbacks
 * — see report-back for the issue list.
 */

import DownloadIcon from '@mui/icons-material/Download';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import LinkIcon from '@mui/icons-material/Link';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Avatar,
  Box,
  GlassButton,
  GlassCard,
  GlassChip,
  MagicInput,
  ShimmerLoader,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import { useProfile, useUpdateProfile } from '../../hooks';
import QuickActionRow from './QuickActionRow';
import RowItem from './RowItem';

interface FormData {
  name: string;
  email: string;
  phone: string;
}

export function ProfileTab(): ReactElement {
  const { t } = useI18n();
  const { consumer, logout } = useConsumerAuth();
  const { data: profile } = useProfile();
  const updateProfile = useUpdateProfile();

  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<FormData | null>(null);

  const baseData: FormData = {
    name: profile?.name ?? consumer?.name ?? '',
    email: profile?.email ?? consumer?.email ?? '',
    phone: profile?.phone ?? '',
  };
  const formData = editedData ?? baseData;

  const displayName = profile?.name ?? consumer?.name ?? 'User';
  const displayEmail = profile?.email ?? consumer?.email ?? '';
  const initial = displayName[0]?.toUpperCase() ?? 'U';
  const memberSince = profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : '—';
  const isVerified = profile?.emailVerified ?? false;

  const handleSave = async () => {
    if (!editedData) return;
    try {
      const payload: { name?: string; phone?: string } = { name: editedData.name };
      if (editedData.phone) payload.phone = editedData.phone;
      await updateProfile.mutateAsync(payload);
      setIsEditing(false);
      setEditedData(null);
    } catch (err) {
      // Mutation error surface is handled by the parent page via mutation.error.
      // biome-ignore lint/suspicious/noConsole: surface in browser telemetry
      console.error('Failed to update profile:', err);
    }
  };

  const handleCancel = () => {
    setEditedData(null);
    setIsEditing(false);
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' },
        gap: 2.25,
        alignItems: 'start',
      }}
    >
      {/* ───── Left: Identity card ───── */}
      <GlassCard sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            {initial}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {/* P2: use subtitle1 (16px, weight 600) so the display name stays
                 inside the h5 page-title ceiling per portals.md §11b2.
                 The h6 variant (14px) read too small for the prominent
                 identity card; subtitle1 sizes it correctly while still
                 capped at the page title. */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {displayName}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
              {displayEmail}
            </Typography>
            {isVerified && (
              <GlassChip
                // P1: verified suffix surfaces what was verified (ID + address)
                // so the chip communicates the actual KYC status, not a generic
                // "Verified" badge.
                label={t('consumer.profile.verifiedChipDetail')}
                size="small"
                color="success"
                icon={<VerifiedIcon />}
                sx={{ mt: 0.75 }}
              />
            )}
          </Box>
        </Box>

        {isEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <MagicInput
              label={t('consumer.profile.fields.fullName')}
              value={formData.name}
              onChange={(e) => setEditedData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <MagicInput
              label={t('consumer.profile.fields.email')}
              value={formData.email}
              disabled
              fullWidth
              helperText={t('profile.emailCannotBeChanged')}
            />
            <MagicInput
              label={t('consumer.profile.fields.phone')}
              value={formData.phone}
              onChange={(e) => setEditedData({ ...formData, phone: e.target.value })}
              fullWidth
            />
          </Box>
        ) : (
          <Box>
            <RowItem label={t('consumer.profile.fields.fullName')} value={baseData.name || '—'} />
            <RowItem label={t('consumer.profile.fields.email')} value={baseData.email || '—'} />
            <RowItem label={t('consumer.profile.fields.phone')} value={baseData.phone || '—'} isLast />
          </Box>
        )}

        <Box sx={{ mt: 2.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {isEditing ? (
            <>
              <GlassButton
                variant="gradient"
                onClick={handleSave}
                disabled={updateProfile.isPending}
                startIcon={
                  updateProfile.isPending ? <ShimmerLoader variant="circular" width={18} height={18} /> : undefined
                }
              >
                {updateProfile.isPending ? t('common.saving') : t('common.saveChanges')}
              </GlassButton>
              <GlassButton variant="outlined" onClick={handleCancel} disabled={updateProfile.isPending}>
                {t('common.cancel')}
              </GlassButton>
            </>
          ) : (
            <>
              <GlassButton variant="outlined" onClick={() => setIsEditing(true)}>
                {t('consumer.profile.actions.editProfile')}
              </GlassButton>
              <GlassButton
                variant="text"
                startIcon={<LockIcon />}
                onClick={() => {
                  // TODO(consumer-profile): wire to password-change flow (auth portal redirect).
                }}
              >
                {t('consumer.profile.actions.changePassword')}
              </GlassButton>
            </>
          )}
        </Box>
      </GlassCard>

      {/* ───── Right: Account + Quick actions stack ───── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <GlassCard sx={{ p: 2 }}>
          <Typography
            variant="overline"
            color="textSecondary"
            sx={{ display: 'block', mb: 1, letterSpacing: '0.08em' }}
          >
            {t('consumer.profile.account.heading')}
          </Typography>
          <RowItem
            label={t('consumer.profile.account.twoFactorAuth')}
            value={t('consumer.profile.account.twoFactorValue')}
            chipLabel="On"
            chipColor="success"
          />
          <RowItem
            label={t('consumer.profile.account.loginSessions')}
            // P6: the `loginSessionsValue` key interpolates `{count}` — passing
            // no arg made the chip render the literal `{{count}} active`.
            // TODO(GH-15): wire to a real sessions count once the
            // /auth/sessions endpoint is consumed here; hardcode 1 (the
            // current device) until then.
            value={t('consumer.profile.account.loginSessionsValue', { count: 1 })}
          />
          <RowItem label={t('consumer.profile.account.memberSince')} value={memberSince} isLast />
          <Box sx={{ mt: 1.5 }}>
            <GlassButton
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => {
                // TODO(consumer-profile): wire "sign out everywhere" — calls
                // /auth/sessions/revoke-all then logs the current device out.
                logout();
              }}
            >
              {t('consumer.profile.actions.signOutEverywhere')}
            </GlassButton>
          </Box>
        </GlassCard>

        <GlassCard sx={{ p: 2 }}>
          <Typography
            variant="overline"
            color="textSecondary"
            sx={{ display: 'block', mb: 1, letterSpacing: '0.08em' }}
          >
            {t('consumer.profile.quickActions.heading')}
          </Typography>
          <QuickActionRow
            icon={<DownloadIcon />}
            label={t('consumer.profile.quickActions.downloadData')}
            onClick={() => {
              // TODO(consumer-profile): wire to GDPR/data-export endpoint.
            }}
          />
          <QuickActionRow
            icon={<LinkIcon />}
            label={t('consumer.profile.quickActions.connectPlaid')}
            subText={t('consumer.profile.quickActions.banksLinked', { count: 3 })}
            onClick={() => {
              // TODO(consumer-profile): wire to Plaid Link / integration service.
            }}
          />
          <QuickActionRow
            icon={<GroupAddIcon />}
            label={t('consumer.profile.quickActions.addCoBorrower')}
            onClick={() => {
              // TODO(consumer-profile): wire to co-borrower invite flow.
            }}
          />
        </GlassCard>
      </Box>
    </Box>
  );
}

export default ProfileTab;
